/**
 * The appearance preference — System, Light or Dark — remembered per browser.
 *
 * A preference about this browser, not a fact about the account, so it lives in localStorage and
 * never on the profile: the same person on a laptop and on a phone may want it two ways, and a
 * shared machine should not push one person's choice onto the next. The same call
 * `useArtifactLayout` makes one folder over, and the same storage key shape.
 *
 * ONE store behind every caller, rather than `useState` per component.
 *
 * That is the whole reason this file is not four lines long. The preference is read in two places
 * at once — the row that changes it, and the shell that paints itself from it — and two components
 * each holding their own `useState` would be two copies of one fact: pressing Light in the row
 * would repaint the row and leave the shell dark, which is exactly the bug this replaced.
 * `useSyncExternalStore` gives every caller the same value and re-renders all of them together.
 */
import { useSyncExternalStore } from 'react';

/** What the menu offers. `system` is a rule, the other two are answers. */
export type Appearance = 'system' | 'light' | 'dark';

/**
 * What `system` resolves TO. A separate type on purpose: everything downstream of this hook paints
 * one of two themes, and a component that accepted `'system'` as a theme would have to re-derive
 * the media query itself — which is the one thing this store exists to do once.
 */
export type ResolvedAppearance = 'light' | 'dark';

export interface AppearanceSnapshot {
  /** What is chosen — including `system`, which is what the menu has to show as selected. */
  appearance: Appearance;
  /** What that choice means right now. `system` folded through the media query. */
  resolved: ResolvedAppearance;
}

const KEY = 'deck:appearance';
const QUERY = '(prefers-color-scheme: dark)';

/**
 * The RESOLVED theme, mirrored into a cookie so the SERVER can render the right one.
 *
 * localStorage is the right home for the preference and the wrong one for this: the server never
 * sees it, so every response was built in the light palette and the reader watched a white page —
 * a white composer card most of all — until React had hydrated and the store had caught up. On a
 * slow load that is seconds, which is exactly the complaint (Beka, 2026-09-04).
 *
 * The `resolved` value and not the preference, because `system` is only answerable with a media
 * query and the server has no media. localStorage stays the source of truth; this is a hint the
 * client keeps up to date for the next request, and a stale one costs a corrected render, not a
 * wrong app.
 *
 * A separate name from `KEY` on purpose — the two hold different things, and a cookie called
 * `deck:appearance` that did not contain the appearance would be a trap.
 */
export const THEME_COOKIE = 'deck_theme';

/**
 * System, and not Dark.
 *
 * v1.5's surfaces are dark today, so Dark is what a new visitor actually sees — but the default has
 * to be the rule rather than the current answer, or the preference lies the moment someone's device
 * says light and they never opened this menu.
 */
const DEFAULT: Appearance = 'system';

/**
 * The pre-hydration answer, and it must be `light` for BOTH halves.
 *
 * The server cannot know what is in a browser's localStorage or what its device prefers, so the
 * first client render has to agree with it or React tears the tree down and rebuilds it.
 *
 * This stays light and stays the honest answer for the STORE. It is no longer what the app renders
 * on the server, though: `AppearanceScope` reads `THEME_COOKIE` out of the request and renders the
 * first pass from that, precisely because the answer here has to be a constant and the cookie does
 * not. See that component for how the two are handed over.
 */
const SERVER_SNAPSHOT: AppearanceSnapshot = { appearance: DEFAULT, resolved: 'light' };

let chosen: Appearance = DEFAULT;
let system: ResolvedAppearance = 'light';

/**
 * Cached, and only ever replaced when something actually changed.
 *
 * `getSnapshot` is called during render and React compares the result by identity — a fresh object
 * every call is an infinite re-render loop, not a fresh value.
 */
let snapshot: AppearanceSnapshot = SERVER_SNAPSHOT;

const listeners = new Set<() => void>();

function publish() {
  const resolved: ResolvedAppearance = chosen === 'system' ? system : chosen;
  if (snapshot.appearance === chosen && snapshot.resolved === resolved) return;
  snapshot = { appearance: chosen, resolved };
  listeners.forEach((notify) => notify());
}

function readStored(): Appearance {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : DEFAULT;
  } catch {
    // Storage refused (private mode, quota, a blocked third-party context).
    return DEFAULT;
  }
}

let started = false;

/**
 * Wired once, on the first subscriber, and never torn down.
 *
 * The media query and the cross-tab listener are one pair for the whole app rather than one pair
 * per component, and unsubscribing the last consumer does not mean the preference stopped being
 * true — the next mount should find the store already correct rather than briefly showing the
 * default again.
 */
function start() {
  if (started || typeof window === 'undefined') return;
  started = true;

  chosen = readStored();

  const mq = window.matchMedia(QUERY);
  const syncSystem = () => {
    system = mq.matches ? 'dark' : 'light';
    publish();
  };
  syncSystem();
  // Not a one-shot read. Someone flipping their OS to dark at sunset is the whole reason `system`
  // is an option, and a value sampled once at mount would sit stale until the next reload.
  mq.addEventListener('change', syncSystem);

  // The preference is per browser, not per tab. `storage` fires in the OTHER tabs, which is exactly
  // the set that has to catch up — the tab that made the change already published to itself.
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== KEY) return;
    chosen = readStored();
    publish();
  });

  publish();
}

function subscribe(notify: () => void): () => void {
  start();
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

function getSnapshot(): AppearanceSnapshot {
  return snapshot;
}

function getServerSnapshot(): AppearanceSnapshot {
  return SERVER_SNAPSHOT;
}

/** Change the preference. Exported on its own so a non-component (a shortcut, a test) can call it. */
export function setAppearance(next: Appearance): void {
  chosen = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    // The choice still holds for this visit; it just will not survive the tab.
  }
  publish();
}

export interface UseAppearance extends AppearanceSnapshot {
  setAppearance: (next: Appearance) => void;
}

export function useAppearance(): UseAppearance {
  const { appearance, resolved } = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { appearance, resolved, setAppearance };
}
